import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  domainNameRenderer,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { DomainIpFormValue, DomainIpItem } from '@nirvana/shared/dtos/types/policies/common';
import { GzExchangeProtectionAntiSpoofingDataProvider } from './GzExchangeProtectionAntiSpoofingDataProvider';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_ANTI_SPOOFING_GRID } from '../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from 'apps/webcomponents/src/policies/common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzExchangeProtectionAntiSpoofingContainerWebComponent } from '../../../helpers/gzExchangeProtectionAntiSpoofingContainer';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_ANTI_SPOOFING_GRID)
export class GzExchangeProtectionAntiSpoofingGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  DomainIpItem,
  DomainIpFormValue,
  GzExchangeProtectionAntiSpoofingDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;

  protected hasEditRowAction = false;
  protected hasBasicEditRowAction = true;

  private currentlyEditedPosition: number;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;
  protected showHeaderCustomActionsInsideGrid = true;
  protected columnRenderers = {
    [ColumnKeys.DOMAIN]: domainNameRenderer.bind(this),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
  };

  protected renderHeaderButtons(): TemplateResult | undefined {
    return html`
      <gz-button
        id="add-button"
        type="${ButtonTypes.OUTLINE}"
        class="inline-menu"
        @click="${this.openAddDialog.bind(this)}"
      >
        ${translateFn('Add')}
      </gz-button>
      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        disabled
        class="inline-menu"
        @click="${this.onDeleteSelectedItems.bind(this)}"
      >
        ${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html` <gz-text fontSize="${FontSizes.PARAGRAPH_13}" fontWeight="${FontWeights.LIGHT}"
        >${translateFn('No rules')}</gz-text
      >
      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        @click="${this.openAddDialog.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>`;
  }

  protected createDialog(
    formComponent: GzExchangeProtectionAntiSpoofingContainerWebComponent,
    title: string,
    confirmText: string,
    callback: (formValue: any) => ValidationResult
  ) {
    const configs: FullPagePopupConfigs = {
      bodyForm: formComponent,
      strings: {
        title,
        confirm: confirmText,
        cancel: translateFn('Cancel'),
      },
      confirmCallbackFn: callback,
    };
    new GzPolicyFullPageWindow(configs).show();
  }

  protected openAddDialog() {
    const addDomainIpCallBack = (domainIpForm: DomainIpItem): ValidationResult => {
      this.onNewGridItemAdd(domainIpForm);
      return { valid: true };
    };
    this.createDialog(
      new GzExchangeProtectionAntiSpoofingContainerWebComponent(
        (this.gridComponent.dataProvider as GzExchangeProtectionAntiSpoofingDataProvider).items
      ),
      translateFn('Add domain'),
      translateFn('Add'),
      addDomainIpCallBack
    );
  }

  protected openEditDialog(rowData: any) {
    const onEditDomainIpCallBack = (domainIpForm: DomainIpItem): ValidationResult => {
      this.gridComponent.propertiesSet = false;
      (this.gridComponent.dataProvider as GzExchangeProtectionAntiSpoofingDataProvider).updateItem(
        this.currentlyEditedPosition,
        domainIpForm
      );
      this.gridComponent.propertiesSet = true;

      return { valid: true };
    };

    this.currentlyEditedPosition = rowData.item.defaultPositionInGrid;

    this.createDialog(
      new GzExchangeProtectionAntiSpoofingContainerWebComponent(
        (this.gridComponent.dataProvider as GzExchangeProtectionAntiSpoofingDataProvider).items,
        rowData.item
      ),
      translateFn('Edit domain'),
      translateFn('Save#preserve state'),
      onEditDomainIpCallBack
    );
  }

  protected getDataProvider(): GzExchangeProtectionAntiSpoofingDataProvider {
    return new GzExchangeProtectionAntiSpoofingDataProvider(this.initialGridItems);
  }

  protected onBasicEditRow(event: CustomEvent) {
    this.openEditDialog(event.detail.rowData);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  protected onGridItemsChange(): void {
    super.onGridItemsChange();
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
  }
}
